import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/lib/cn';

export type ButtonVariant = 'default' | 'primary' | 'destructive' | 'outline' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
  children: ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  default: 'bg-[#6B0F1A] text-white hover:bg-[#520b14] focus-visible:ring-[#6B0F1A]',
  primary: 'bg-[#6B0F1A] text-white hover:bg-[#520b14] focus-visible:ring-[#6B0F1A]',
  destructive: 'bg-[#c62828] text-white hover:bg-[#a31f1f] focus-visible:ring-[#c62828]',
  outline:
    'border border-[#ddd] bg-white text-[#333] hover:bg-[#f5f5f5] focus-visible:ring-[#6B0F1A]',
  ghost:
    'bg-transparent text-[#666] hover:bg-[#f0f0f0] hover:text-[#333] focus-visible:ring-[#6B0F1A]',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs rounded-md',
  md: 'px-4 py-2 text-sm rounded-lg',
  lg: 'px-6 py-3 text-base rounded-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { variant = 'default', size = 'md', asChild = false, className, disabled, children, ...props },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center font-semibold transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        disabled={disabled}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);

Button.displayName = 'Button';
